import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Tag, Printer, Plus, Trash2, Upload, Download, Settings, Eye, Copy, FileSpreadsheet, FileDown, Pencil, Save, Package } from 'lucide-react';
import { InventoryLabelPicker } from '../components/InventoryLabelPicker';
import { rowsToLabelProducts } from '../utils/labelImport';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { useReactToPrint } from 'react-to-print';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { todayCR } from '../utils/dateHelpers';

// Format price with Costa Rica colón symbol
const variantText = (p) => [p.size && `Talla: ${p.size}`, p.color && `Color: ${p.color}`].filter(Boolean).join(' · ');

const formatPrice = (price) => {
  if (!price) return '';
  // Remove any existing currency symbols and spaces
  const numericValue = String(price).replace(/[₡$,\s]/g, '');
  const number = parseFloat(numericValue);
  if (isNaN(number)) return price; // Return original if not a number
  // Format with colón symbol and thousand separators
  return `₡ ${number.toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Predefined label designs
const LABEL_DESIGNS = [
  {
    id: 'minimal',
    name: 'Minimalista',
    description: 'Solo código de barras',
    preview: 'bg-white border-2 border-gray-200'
  },
  {
    id: 'classic',
    name: 'Clásico',
    description: 'Nombre + Código + Precio',
    preview: 'bg-white border-2 border-gray-300'
  },
  {
    id: 'modern',
    name: 'Moderno',
    description: 'Diseño con bordes redondeados',
    preview: 'bg-gradient-to-b from-gray-50 to-white border-2 border-blue-200 rounded-lg'
  },
  {
    id: 'bold',
    name: 'Destacado',
    description: 'Precio grande y visible',
    preview: 'bg-yellow-50 border-2 border-yellow-400'
  },
  {
    id: 'compact',
    name: 'Compacto',
    description: 'Información condensada',
    preview: 'bg-gray-100 border border-gray-400'
  }
];

// Barcode formats
const BARCODE_FORMATS = [
  { id: 'CODE128', name: 'Code 128', description: 'Alfanumérico - Más versátil' },
  { id: 'EAN13', name: 'EAN-13', description: '13 dígitos - Productos retail' },
  { id: 'EAN8', name: 'EAN-8', description: '8 dígitos - Productos pequeños' },
  { id: 'UPC', name: 'UPC-A', description: '12 dígitos - Estándar USA' },
  { id: 'CODE39', name: 'Code 39', description: 'Alfanumérico - Industrial' },
  { id: 'QR', name: 'QR Code', description: 'Puede almacenar más datos' }
];

// Common label sizes for thermal printers (in mm)
const LABEL_SIZES = [
  { id: '50x30', name: '50 x 30 mm (5cm x 3cm)', width: 50, height: 30 },
  { id: '40x30', name: '40 x 30 mm', width: 40, height: 30 },
  { id: '50x40', name: '50 x 40 mm', width: 50, height: 40 },
  { id: '58x40', name: '58 x 40 mm', width: 58, height: 40 },
  { id: '40x20', name: '40 x 20 mm', width: 40, height: 20 },
  { id: '33x15', name: '33 x 15 mm (joyería/pequeña)', width: 33, height: 15 },
  { id: '100x50', name: '100 x 50 mm (envío/grande)', width: 100, height: 50 },
  { id: 'custom', name: 'Personalizado', width: 50, height: 30 }
];

const LIST_KEY = 'palistore_label_generator_list';

export default function LabelGenerator() {
  const [products, setProducts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LIST_KEY)) || []; } catch { return []; }
  });
  const [showInventory, setShowInventory] = useState(false);
  useEffect(() => { localStorage.setItem(LIST_KEY, JSON.stringify(products)); }, [products]);
  const [currentProduct, setCurrentProduct] = useState({
    name: '',
    code: '',
    price: '',
    description: '',
    size: '',
    color: '',
    quantity: 1
  });
  const [editingId, setEditingId] = useState(null);
  const [settings, setSettings] = useState({
    design: 'classic',
    barcodeFormat: 'CODE128',
    labelSize: '50x30',
    customWidth: 50,
    customHeight: 30,
    showName: true,
    showCode: true,
    showPrice: true,
    showDescription: false,
    fontSize: 'medium',
    barcodeHeight: 35,
    barcodeWidth: 2.5, // Thicker bars for better scanner readability
    orientation: 'horizontal'  // 'horizontal' (50x30) or 'vertical' (30x50)
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const printRef = useRef(null);

  // Build print CSS so the label prints EXACTLY as the preview: @page size
  // matching the label, zero page/body/label margins & padding, no scaling.
  const buildPageStyle = () => {
    const size = LABEL_SIZES.find(s => s.id === settings.labelSize) || LABEL_SIZES[0];
    const w = settings.labelSize === 'custom' ? settings.customWidth : size.width;
    const h = settings.labelSize === 'custom' ? settings.customHeight : size.height;
    const isLandscape = settings.orientation === 'horizontal';
    const pw = isLandscape ? w : h;
    const ph = isLandscape ? h : w;
    return `
      @page { size: ${pw}mm ${ph}mm; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          width: ${pw}mm !important;
          background: #ffffff;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .print-labels-container { margin: 0 !important; padding: 0 !important; display: block !important; }
        .thermal-label {
          width: ${pw}mm !important;
          height: ${ph}mm !important;
          margin: 0 !important;
          padding: 0 !important;
          transform: none !important;
          zoom: 1 !important;
          box-sizing: border-box;
          overflow: hidden;
          display: block !important;
          visibility: visible !important;
          background: #ffffff !important;
          page-break-after: always;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .thermal-label, .thermal-label * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
          visibility: visible !important;
        }
        .thermal-label-name, .thermal-label-price, .thermal-label-desc { color: #000000 !important; }
        .thermal-label:last-child { page-break-after: auto; }
      }
    `;
  };

  // Handle print
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Etiquetas',
    pageStyle: buildPageStyle(),
    onAfterPrint: () => toast.success('Etiquetas enviadas a imprimir')
  });

  // Generate all labels for printing (moved up for use in handleDownloadPdf)
  const generateLabelsForPrint = () => {
    const allLabels = [];
    products.forEach(product => {
      for (let i = 0; i < product.quantity; i++) {
        allLabels.push({ ...product, printIndex: i });
      }
    });
    return allLabels;
  };

  // Generate PDF for download
  const handleDownloadPdf = async () => {
    if (products.length === 0) {
      toast.error('Agrega productos primero');
      return;
    }
    
    setGeneratingPdf(true);
    toast.info('Generando PDF...');
    
    try {
      const labelsToExport = generateLabelsForPrint();
      const isLandscape = settings.orientation === 'horizontal';
      const pageWidth = isLandscape ? 50 : 30; // mm
      const pageHeight = isLandscape ? 30 : 50; // mm
      const pdfMinMm = Math.min(pageWidth, pageHeight);
      const pdfPricePt = pdfMinMm <= 15 ? 8 : pdfMinMm <= 30 ? 13 : pdfMinMm <= 40 ? 15 : 18;
      
      // Create PDF with exact label dimensions
      const pdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight]
      });
      
      for (let i = 0; i < labelsToExport.length; i++) {
        const label = labelsToExport[i];
        
        if (i > 0) {
          pdf.addPage([pageWidth, pageHeight], isLandscape ? 'landscape' : 'portrait');
        }
        
        // Create a temporary container for rendering
        const tempDiv = document.createElement('div');
        tempDiv.style.cssText = `
          position: fixed;
          left: -9999px;
          top: 0;
          width: ${pageWidth}mm;
          height: ${pageHeight}mm;
          background: white;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.5mm;
          overflow: hidden;
          box-sizing: border-box;
          font-family: Arial, sans-serif;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        `;
        
        // Add content
        let contentHtml = '';
        
        if (settings.showName && label.name) {
          contentHtml += `<div style="font-size: 7pt; font-weight: bold; line-height: 1.05; margin-bottom: 0.5mm; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #000;">${label.name}</div>`;
        }
        
        // Create barcode canvas
        const barcodeCanvas = document.createElement('canvas');
        if (settings.barcodeFormat !== 'QR' && label.code) {
          try {
            JsBarcode(barcodeCanvas, label.code, {
              format: settings.barcodeFormat,
              width: settings.barcodeWidth || 2.5,
              height: 28,
              displayValue: settings.showCode,
              fontSize: 9,
              margin: 4,
              background: '#ffffff',
              lineColor: '#000000'
            });
          } catch (e) {
            console.error('Barcode error:', e);
          }
        }
        
        tempDiv.innerHTML = contentHtml;
        
        // Add barcode image
        if (settings.barcodeFormat !== 'QR' && label.code) {
          const barcodeImg = document.createElement('img');
          barcodeImg.src = barcodeCanvas.toDataURL();
          barcodeImg.style.cssText = `max-width: 95%; max-height: ${variantText(label) ? 9 : 11}mm; margin: 0.3mm 0;`;
          tempDiv.appendChild(barcodeImg);
        } else if (settings.barcodeFormat === 'QR' && label.code) {
          const qrCanvas = document.createElement('canvas');
          await QRCode.toCanvas(qrCanvas, label.code, { width: 80, margin: 2 });
          const qrImg = document.createElement('img');
          qrImg.src = qrCanvas.toDataURL();
          qrImg.style.cssText = 'width: 11mm; height: 11mm; margin: 0.3mm 0;';
          tempDiv.appendChild(qrImg);
        }
        
        // Add price
        if (settings.showPrice && label.price) {
          const priceDiv = document.createElement('div');
          priceDiv.style.cssText = `font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: ${pdfPricePt}pt; font-weight: 700; letter-spacing: 0.2px; line-height: 1.1; margin-top: 0.3mm; color: #000000;`;
          priceDiv.textContent = formatPrice(label.price);
          tempDiv.appendChild(priceDiv);
        }
        
        // Add talla / color
        if (variantText(label)) {
          const varDiv = document.createElement('div');
          varDiv.style.cssText = "font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 6pt; font-weight: 700; line-height: 1.05; margin-top: 0.2mm; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #000000;";
          varDiv.textContent = variantText(label);
          tempDiv.appendChild(varDiv);
        }

        // Add description
        if (settings.showDescription && label.description) {
          const descDiv = document.createElement('div');
          descDiv.style.cssText = "font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 5pt; font-weight: 300; line-height: 1.05; margin-top: 0.2mm; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #000000;";
          descDiv.textContent = label.description;
          tempDiv.appendChild(descDiv);
        }
        
        document.body.appendChild(tempDiv);
        
        // Wait for images to load
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Capture as canvas
        const canvas = await html2canvas(tempDiv, {
          scale: 3,
          useCORS: true,
          backgroundColor: '#ffffff',
          width: pageWidth * 3.78,
          height: pageHeight * 3.78
        });
        
        // Add to PDF
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
        
        // Cleanup
        document.body.removeChild(tempDiv);
      }
      
      // Download
      pdf.save(`etiquetas_${todayCR()}.pdf`);
      toast.success(`PDF generado con ${labelsToExport.length} etiqueta(s)`);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Error al generar PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Add product to list
  const addProduct = () => {
    if (!currentProduct.code) {
      toast.error('El código es requerido');
      return;
    }
    if (editingId) {
      setProducts(products.map(p => (p.id === editingId ? { ...currentProduct, id: editingId } : p)));
      toast.success('Producto actualizado');
    } else {
      setProducts([...products, { ...currentProduct, id: Date.now() }]);
      toast.success('Producto agregado');
    }
    setEditingId(null);
    setCurrentProduct({ name: '', code: '', price: '', description: '', size: '', color: '', quantity: 1 });
  };

  const editProduct = (product) => {
    setEditingId(product.id);
    setCurrentProduct({ size: '', color: '', ...product });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setCurrentProduct({ name: '', code: '', price: '', description: '', size: '', color: '', quantity: 1 });
  };

  // Remove product
  const removeProduct = (id) => {
    setProducts(products.filter(p => p.id !== id));
  };

  // Duplicate product
  const duplicateProduct = (product) => {
    setProducts([...products, { ...product, id: Date.now() }]);
  };

  // Import from Excel/CSV
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      // Handle Excel files
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const imported = rowsToLabelProducts(jsonData);
          
          if (imported.length > 0) {
            setProducts(prev => [...prev, ...imported]);
            toast.success(`${imported.length} productos importados desde Excel`);
            setShowImport(false);
          } else {
            toast.error('No se encontraron productos en el archivo');
          }
        } catch (error) {
          console.error('Excel import error:', error);
          toast.error('Error al leer el archivo Excel');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Handle CSV/TXT files
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          const imported = rowsToLabelProducts(lines.map(l => l.split(/[,;\t]/)));
          
          if (imported.length > 0) {
            setProducts(prev => [...prev, ...imported]);
            toast.success(`${imported.length} productos importados`);
            setShowImport(false);
          } else {
            toast.error('No se encontraron productos en el archivo');
          }
        } catch (error) {
          toast.error('Error al importar archivo');
        }
      };
      reader.readAsText(file);
    }
    
    // Reset file input
    e.target.value = '';
  };

  // Download Excel template
  const downloadTemplate = () => {
    const templateData = [
      ['NOMBRE', 'CODIGO', 'PRECIO', 'DESCRIPCION', 'CANTIDAD', 'TALLA', 'COLOR'],
      ['Blusa Ejemplo', '7501055303045', '15000', 'Descripción opcional', 5, 'M', 'Rojo'],
      ['Pantalón Ejemplo', '123456789012', '8500', '', 3, '32', 'Azul'],
      ['Palillos', '111222333444', '1500', 'Palillos de madera', 10, '', '']
    ];
    
    const ws = XLSX.utils.aoa_to_array ? XLSX.utils.aoa_to_sheet(templateData) : XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Productos');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // NOMBRE
      { wch: 15 }, // CODIGO
      { wch: 10 }, // PRECIO
      { wch: 25 }, // DESCRIPCION
      { wch: 10 }, // CANTIDAD
      { wch: 8 },  // TALLA
      { wch: 12 }  // COLOR
    ];
    
    XLSX.writeFile(wb, 'plantilla_etiquetas.xlsx');
    toast.success('Plantilla Excel descargada');
  };

  // Get label dimensions in pixels (for screen preview, 96 DPI)
  const getLabelDimensions = () => {
    const size = LABEL_SIZES.find(s => s.id === settings.labelSize) || LABEL_SIZES[0];
    const width = settings.labelSize === 'custom' ? settings.customWidth : size.width;
    const height = settings.labelSize === 'custom' ? settings.customHeight : size.height;
    // Convert mm to pixels (approximately 3.78 pixels per mm at 96 DPI)
    return {
      width: Math.round(width * 3.78),
      height: Math.round(height * 3.78),
      widthMm: width,
      heightMm: height
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
              <Tag className="w-8 h-8 text-blue-600" />
              Generador de Etiquetas
            </h1>
            <p className="text-slate-600 mt-1">Compatible con CHITENG CT220F y otras impresoras térmicas</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="settings-btn">
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </Button>
            <Button variant="outline" onClick={() => setShowInventory(true)} data-testid="from-inventory-btn">
              <Package className="w-4 h-4 mr-2" />
              Desde Inventario
            </Button>
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-btn">
              <Upload className="w-4 h-4 mr-2" />
              Importar
            </Button>
            {products.length > 0 && (
              <>
                <Button variant="outline" onClick={() => setShowPreview(true)} data-testid="preview-btn">
                  <Eye className="w-4 h-4 mr-2" />
                  Vista Previa
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleDownloadPdf} 
                  disabled={generatingPdf}
                  data-testid="download-pdf-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  {generatingPdf ? 'Generando...' : 'Descargar PDF'}
                </Button>
                <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700" data-testid="print-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir ({generateLabelsForPrint().length})
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Add Product Form */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Agregar Producto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nombre del Producto</Label>
                <Input
                  value={currentProduct.name}
                  onChange={(e) => setCurrentProduct({ ...currentProduct, name: e.target.value })}
                  placeholder="Ej: Coca Cola 500ml"
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label>Código *</Label>
                <Input
                  value={currentProduct.code}
                  onChange={(e) => setCurrentProduct({ ...currentProduct, code: e.target.value })}
                  placeholder="Ej: 7501055303045"
                  data-testid="product-code-input"
                />
              </div>
              <div>
                <Label>Precio</Label>
                <Input
                  value={currentProduct.price}
                  onChange={(e) => setCurrentProduct({ ...currentProduct, price: e.target.value })}
                  placeholder="Ej: 15000"
                  data-testid="product-price-input"
                />
              </div>
              <div>
                <Label>Descripción (opcional)</Label>
                <Input
                  value={currentProduct.description}
                  onChange={(e) => setCurrentProduct({ ...currentProduct, description: e.target.value })}
                  placeholder="Ej: Bebida gaseosa"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Talla (opcional)</Label>
                  <Input
                    value={currentProduct.size || ''}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, size: e.target.value })}
                    placeholder="Ej: M, 32, 10"
                    maxLength={15}
                    data-testid="product-size-input"
                  />
                </div>
                <div>
                  <Label>Color (opcional)</Label>
                  <Input
                    value={currentProduct.color || ''}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, color: e.target.value })}
                    placeholder="Ej: Rojo"
                    maxLength={20}
                    data-testid="product-color-input"
                  />
                </div>
              </div>
              <div>
                <Label>Cantidad de Etiquetas</Label>
                <Input
                  type="number"
                  min="1"
                  value={currentProduct.quantity}
                  onChange={(e) => setCurrentProduct({ ...currentProduct, quantity: parseInt(e.target.value) || 1 })}
                  data-testid="product-quantity-input"
                />
              </div>
              <Button onClick={addProduct} className="w-full" data-testid="add-product-btn">
                {editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {editingId ? 'Guardar cambios' : 'Agregar a Lista'}
              </Button>
              {editingId && (
                <Button variant="outline" onClick={cancelEdit} className="w-full" data-testid="cancel-edit-btn">
                  Cancelar edición
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Product List */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">
                Lista de Productos ({products.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Tag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No hay productos agregados</p>
                  <p className="text-sm">Agrega productos manualmente o importa desde Excel</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${editingId === product.id ? 'bg-blue-50 border-blue-300' : 'bg-slate-50'}`}
                      data-testid={`label-product-${product.id}`}
                    >
                      <div className="flex-1">
                        <p className="font-medium">{product.name || 'Sin nombre'}</p>
                        <p className="text-sm text-slate-600">
                          Código: {product.code} | Precio: {product.price || 'N/A'} | Cantidad: {product.quantity}
                        </p>
                        {variantText(product) && <p className="text-xs text-slate-500">{variantText(product)}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => editProduct(product)}
                          title="Editar"
                          data-testid={`edit-label-product-${product.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => duplicateProduct(product)}
                          title="Duplicar"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeProduct(product.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Current Settings Display */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                Diseño: {LABEL_DESIGNS.find(d => d.id === settings.design)?.name}
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
                Formato: {BARCODE_FORMATS.find(f => f.id === settings.barcodeFormat)?.name}
              </span>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full">
                Tamaño: {settings.labelSize === 'custom' 
                  ? `${settings.customWidth} x ${settings.customHeight} mm` 
                  : LABEL_SIZES.find(s => s.id === settings.labelSize)?.name}
              </span>
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full">
                Orientación: {settings.orientation === 'horizontal' ? 'Horizontal' : 'Vertical'}
              </span>
              <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full">
                Total etiquetas: {generateLabelsForPrint().length}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Settings Dialog */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Configuración de Etiquetas</DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="design" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="design">Diseño</TabsTrigger>
                <TabsTrigger value="barcode">Código</TabsTrigger>
                <TabsTrigger value="size">Tamaño</TabsTrigger>
              </TabsList>
              
              <TabsContent value="design" className="space-y-4 mt-4">
                <Label className="text-base font-semibold">Seleccionar Diseño</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {LABEL_DESIGNS.map((design) => (
                    <div
                      key={design.id}
                      onClick={() => setSettings({ ...settings, design: design.id })}
                      className={`p-4 rounded-lg cursor-pointer border-2 transition-all ${
                        settings.design === design.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`h-16 mb-2 ${design.preview}`}></div>
                      <p className="font-medium text-sm">{design.name}</p>
                      <p className="text-xs text-slate-500">{design.description}</p>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-3 pt-4 border-t">
                  <Label className="text-base font-semibold">Campos a Mostrar</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={settings.showName}
                        onCheckedChange={(checked) => setSettings({ ...settings, showName: checked })}
                      />
                      <span>Nombre del producto</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={settings.showCode}
                        onCheckedChange={(checked) => setSettings({ ...settings, showCode: checked })}
                      />
                      <span>Código/Número</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={settings.showPrice}
                        onCheckedChange={(checked) => setSettings({ ...settings, showPrice: checked })}
                      />
                      <span>Precio</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={settings.showDescription}
                        onCheckedChange={(checked) => setSettings({ ...settings, showDescription: checked })}
                      />
                      <span>Descripción</span>
                    </label>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="barcode" className="space-y-4 mt-4">
                <Label className="text-base font-semibold">Formato de Código de Barras</Label>
                <div className="grid grid-cols-2 gap-3">
                  {BARCODE_FORMATS.map((format) => (
                    <div
                      key={format.id}
                      onClick={() => setSettings({ ...settings, barcodeFormat: format.id })}
                      className={`p-4 rounded-lg cursor-pointer border-2 transition-all ${
                        settings.barcodeFormat === format.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-medium">{format.name}</p>
                      <p className="text-xs text-slate-500">{format.description}</p>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4 border-t space-y-4">
                  <div>
                    <Label>Altura del Código de Barras (px)</Label>
                    <Input
                      type="number"
                      min="30"
                      max="100"
                      value={settings.barcodeHeight}
                      onChange={(e) => setSettings({ ...settings, barcodeHeight: parseInt(e.target.value) || 50 })}
                    />
                    <p className="text-xs text-slate-500 mt-1">Recomendado: 50px o más para mejor lectura</p>
                  </div>
                  
                  <div>
                    <Label>Grosor de Barras</Label>
                    <Select
                      value={String(settings.barcodeWidth)}
                      onValueChange={(value) => setSettings({ ...settings, barcodeWidth: parseFloat(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1.5">Fino (1.5) - Etiquetas pequeñas</SelectItem>
                        <SelectItem value="2">Normal (2)</SelectItem>
                        <SelectItem value="2.5">Grueso (2.5) - Recomendado</SelectItem>
                        <SelectItem value="3">Extra grueso (3) - Mejor lectura</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Barras más gruesas = mejor lectura con pistola</p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="size" className="space-y-4 mt-4">
                <Label className="text-base font-semibold">Tamaño de Etiqueta</Label>
                <p className="text-sm text-slate-500">Compatible con CHITENG CT220F (20-58mm de ancho)</p>
                
                <Select
                  value={settings.labelSize}
                  onValueChange={(value) => setSettings({ ...settings, labelSize: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LABEL_SIZES.map((size) => (
                      <SelectItem key={size.id} value={size.id}>
                        {size.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {settings.labelSize === 'custom' && (
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                      <Label>Ancho (mm)</Label>
                      <Input
                        type="number"
                        min="20"
                        max="58"
                        value={settings.customWidth}
                        onChange={(e) => setSettings({ ...settings, customWidth: parseInt(e.target.value) || 40 })}
                      />
                    </div>
                    <div>
                      <Label>Alto (mm)</Label>
                      <Input
                        type="number"
                        min="10"
                        max="100"
                        value={settings.customHeight}
                        onChange={(e) => setSettings({ ...settings, customHeight: parseInt(e.target.value) || 30 })}
                      />
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <Label className="text-base font-semibold">Orientación de Impresión</Label>
                  <p className="text-sm text-slate-500 mb-2">Selecciona según cómo están puestas las etiquetas en tu impresora</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      onClick={() => setSettings({ ...settings, orientation: 'horizontal' })}
                      className={`p-4 rounded-lg cursor-pointer border-2 transition-all text-center ${
                        settings.orientation === 'horizontal'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-16 h-10 bg-gray-200 border border-gray-400 mx-auto mb-2 flex items-center justify-center text-xs">
                        50×30
                      </div>
                      <p className="font-medium text-sm">Horizontal</p>
                      <p className="text-xs text-slate-500">Ancho mayor (50mm×30mm)</p>
                    </div>
                    <div
                      onClick={() => setSettings({ ...settings, orientation: 'vertical' })}
                      className={`p-4 rounded-lg cursor-pointer border-2 transition-all text-center ${
                        settings.orientation === 'vertical'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-10 h-16 bg-gray-200 border border-gray-400 mx-auto mb-2 flex items-center justify-center text-xs">
                        30×50
                      </div>
                      <p className="font-medium text-sm">Vertical</p>
                      <p className="text-xs text-slate-500">Alto mayor (30mm×50mm)</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Label>Tamaño de Fuente</Label>
                  <Select
                    value={settings.fontSize}
                    onValueChange={(value) => setSettings({ ...settings, fontSize: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Pequeño</SelectItem>
                      <SelectItem value="medium">Mediano</SelectItem>
                      <SelectItem value="large">Grande</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter>
              <Button onClick={() => setShowSettings(false)}>Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <InventoryLabelPicker
          open={showInventory}
          onOpenChange={setShowInventory}
          onAdd={(items) => setProducts(prev => [...prev, ...items])}
        />

        {/* Import Dialog */}
        <Dialog open={showImport} onOpenChange={setShowImport}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                Importar Productos desde Excel
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p className="font-semibold text-blue-800 mb-2">Formato del archivo:</p>
                <table className="w-full text-xs text-blue-700">
                  <thead>
                    <tr className="border-b border-blue-200">
                      <th className="py-1 text-left">Columna</th>
                      <th className="py-1 text-left">Ejemplo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>A: NOMBRE</td><td>Palillos</td></tr>
                    <tr><td>B: CODIGO</td><td>7501055303045</td></tr>
                    <tr><td>C: PRECIO</td><td>15000</td></tr>
                    <tr><td>D: DESCRIPCION</td><td>(opcional)</td></tr>
                    <tr><td>E: CANTIDAD</td><td>5</td></tr>
                    <tr><td>F: TALLA</td><td>M (opcional)</td></tr>
                    <tr><td>G: COLOR</td><td>Rojo (opcional)</td></tr>
                  </tbody>
                </table>
              </div>
              
              <div className="border-2 border-dashed border-green-300 rounded-lg p-6 text-center bg-green-50 hover:bg-green-100 transition-colors">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv,.txt"
                  onChange={handleImport}
                  className="hidden"
                  id="import-file"
                />
                <label htmlFor="import-file" className="cursor-pointer">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p className="text-sm font-medium text-green-700">Click para seleccionar archivo</p>
                  <p className="text-xs text-green-600 mt-1">Excel (.xlsx, .xls) o CSV</p>
                </label>
              </div>
              
              <Button variant="outline" onClick={downloadTemplate} className="w-full gap-2">
                <Download className="w-4 h-4" />
                Descargar Plantilla Excel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Vista Previa de Etiquetas</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Real size preview - 1:1 scale */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  📐 Tamaño Real ({settings.orientation === 'horizontal' ? '50mm × 30mm' : '30mm × 50mm'}):
                </p>
                <div className="flex justify-center">
                  <div 
                    className="bg-white shadow-lg border-2 border-dashed border-blue-400"
                    style={{
                      width: settings.orientation === 'horizontal' ? '50mm' : '30mm',
                      height: settings.orientation === 'horizontal' ? '30mm' : '50mm',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      padding: '2mm',
                      boxSizing: 'border-box'
                    }}
                  >
                    {generateLabelsForPrint()[0] && (
                      <RealSizeLabel product={generateLabelsForPrint()[0]} settings={settings} />
                    )}
                  </div>
                </div>
                <p className="text-xs text-center text-blue-600 mt-2">
                  Esta es la dimensión exacta que tendrá tu etiqueta al imprimir
                </p>
              </div>
              
              {/* Grid preview */}
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Vista de todas las etiquetas:</p>
                <div className="flex flex-wrap gap-4 justify-center">
                  {generateLabelsForPrint().slice(0, 12).map((product, idx) => (
                    <LabelPreview
                      key={`${product.id}-${idx}`}
                      product={product}
                      settings={settings}
                      dimensions={getLabelDimensions()}
                    />
                  ))}
                </div>
                {generateLabelsForPrint().length > 12 && (
                  <p className="text-center mt-4 text-slate-500">
                    ... y {generateLabelsForPrint().length - 12} etiquetas más
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreview(false)}>Cerrar</Button>
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
                <Printer className="w-4 h-4 mr-2" />
                Imprimir Todo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Print Area — kept rendered off-screen (NOT display:none) so
            react-to-print captures real content dimensions and doesn't print blank */}
        <div style={{ position: 'absolute', left: '-10000px', top: 0, width: 'auto', height: 'auto', overflow: 'hidden' }} aria-hidden="true">
          <div ref={printRef}>
            <PrintableLabels
              labels={generateLabelsForPrint()}
              settings={settings}
              dimensions={getLabelDimensions()}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Label Preview Component - Shows how label will look (exact print preview)
function LabelPreview({ product, settings, dimensions }) {
  const barcodeRef = useRef(null);
  const qrRef = useRef(null);

  useEffect(() => {
    if (settings.barcodeFormat === 'QR') {
      if (qrRef.current) {
        QRCode.toCanvas(qrRef.current, product.code || '0000', {
          width: 60,
          margin: 2
        });
      }
    } else {
      if (barcodeRef.current && product.code) {
        try {
          // Use same settings as PrintableLabel for accurate preview
          JsBarcode(barcodeRef.current, product.code, {
            format: settings.barcodeFormat,
            width: settings.barcodeWidth || 2.5,
            height: settings.barcodeHeight || 35,
            displayValue: settings.showCode,
            fontSize: 9,
            margin: 5,
            background: '#ffffff',
            lineColor: '#000000',
            flat: true
          });
        } catch (e) {
          console.error('Barcode error:', e);
        }
      }
    }
  }, [product, settings, dimensions]);

  const getDesignStyles = () => {
    switch (settings.design) {
      case 'minimal':
        return 'bg-white';
      case 'classic':
        return 'bg-white border border-gray-300';
      case 'modern':
        return 'bg-gradient-to-b from-gray-50 to-white border border-blue-200 rounded-lg';
      case 'bold':
        return 'bg-yellow-50 border-2 border-yellow-400';
      case 'compact':
        return 'bg-gray-50 border border-gray-400';
      default:
        return 'bg-white border border-gray-200';
    }
  };

  // Size based on orientation - scaled for screen (3.78 px/mm at 96 DPI)
  const isLandscape = settings.orientation === 'horizontal';
  const previewWidth = isLandscape ? 189 : 113; // 50mm or 30mm
  const previewHeight = isLandscape ? 113 : 189; // 30mm or 50mm

  return (
    <div
      className={`flex flex-col items-center justify-center overflow-hidden ${getDesignStyles()}`}
      style={{
        width: previewWidth,
        height: previewHeight,
        minWidth: previewWidth,
        minHeight: previewHeight
      }}
    >
      {settings.showName && product.name && (
        <p className="font-bold text-center w-full text-xs px-1">
          {product.name}
        </p>
      )}
      
      {settings.barcodeFormat === 'QR' ? (
        <canvas ref={qrRef} className="mx-auto block" />
      ) : (
        <svg ref={barcodeRef} className="mx-auto block" style={{ maxWidth: '95%' }} />
      )}
      
      {settings.showPrice && product.price && (
        <p className="text-center w-full text-base text-black" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.2px' }}>
          {formatPrice(product.price)}
        </p>
      )}
      
      {variantText(product) && (
        <p className="text-center truncate w-full px-1 text-black" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, fontSize: '9px' }} data-testid="label-preview-variant">
          {variantText(product)}
        </p>
      )}

      {settings.showDescription && product.description && (
        <p className="text-center truncate w-full px-1" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 300, fontSize: '9px', color: '#4b5563' }}>
          {product.description}
        </p>
      )}
    </div>
  );
}

// Printable Labels Component - Fixed for 50x30mm thermal labels
function PrintableLabels({ labels, settings, dimensions }) {
  const isLandscape = settings.orientation === 'horizontal';
  const wMm = dimensions?.widthMm || 50;
  const hMm = dimensions?.heightMm || 30;
  const pageWidth = `${isLandscape ? wMm : hMm}mm`;
  const pageHeight = `${isLandscape ? hMm : wMm}mm`;
  // Escala del precio segun el tamano de etiqueta (mas grande y legible en termica)
  const minMm = Math.min(wMm, hMm);
  const pricePt = minMm <= 15 ? 8 : minMm <= 30 ? 13 : minMm <= 40 ? 15 : 18;
  
  return (
    <div className="print-labels-container">
      <style>
        {`
          @media print {
            @page {
              size: ${pageWidth} ${pageHeight};
              margin: 0;
            }
            
            html, body {
              margin: 0;
              padding: 0;
              background: white;
            }
            
            .print-labels-container {
              margin: 0;
              padding: 0;
            }
            
            .thermal-label {
              width: ${pageWidth};
              height: ${pageHeight};
              margin: 0;
              padding: 0;
              page-break-after: always;
              page-break-inside: avoid;
              background: white;
              position: relative;
              overflow: hidden;
              box-sizing: border-box;
            }
            
            .thermal-label:last-child {
              page-break-after: auto;
            }
            
            .thermal-label-inner {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: calc(${pageWidth} - 2mm);
              max-height: calc(${pageHeight} - 1mm);
              text-align: center;
              box-sizing: border-box;
              overflow: hidden;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .thermal-label-name {
              font-family: Arial, sans-serif;
              font-size: 7pt;
              font-weight: bold;
              line-height: 1.05;
              margin-bottom: 0.5mm;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              color: #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .thermal-label-barcode {
              display: block;
              margin: 0 auto;
              max-width: 100%;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .thermal-label-barcode img {
              display: block;
              margin: 0 auto;
              max-width: calc(${pageWidth} - 4mm);
              image-rendering: -webkit-optimize-contrast;
              image-rendering: crisp-edges;
              -ms-interpolation-mode: nearest-neighbor;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .thermal-label-price {
              font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
              font-size: ${pricePt}pt;
              font-weight: 700;
              font-variant-numeric: normal;
              letter-spacing: 0.2px;
              line-height: 1.1;
              margin-top: 0.5mm;
              color: #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .thermal-label-desc {
              font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
              font-size: 5pt;
              font-weight: 300;
              line-height: 1.05;
              margin-top: 0.2mm;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              color: #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        `}
      </style>
      {labels.map((label, idx) => (
        <div key={`print-${label.id}-${idx}`} className="thermal-label">
          <PrintableLabel product={label} settings={settings} />
        </div>
      ))}
    </div>
  );
}

// Single Printable Label - HIGH RESOLUTION for thermal printers
function PrintableLabel({ product, settings }) {
  const canvasRef = useRef(null);
  const [barcodeDataUrl, setBarcodeDataUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    if (settings.barcodeFormat === 'QR') {
      // High resolution QR for printing (300 DPI)
      QRCode.toDataURL(product.code || '0000', {
        width: 300, // High resolution
        margin: 2,
        errorCorrectionLevel: 'H'
      }).then(setQrDataUrl);
    } else {
      if (product.code) {
        try {
          // Create high-resolution canvas for barcode (300 DPI equivalent)
          const canvas = document.createElement('canvas');
          
          // Generate barcode with high resolution settings
          JsBarcode(canvas, product.code, {
            format: settings.barcodeFormat,
            width: 3, // Thicker bars
            height: 80, // Taller for better resolution
            displayValue: settings.showCode,
            fontSize: 24, // Larger font for clarity
            margin: 15, // Good quiet zone
            textAlign: 'center',
            textMargin: 5,
            background: '#ffffff',
            lineColor: '#000000'
          });
          
          // Convert to high-quality data URL
          setBarcodeDataUrl(canvas.toDataURL('image/png', 1.0));
        } catch (e) {
          console.error('Barcode generation error:', e);
        }
      }
    }
  }, [product, settings]);

  return (
    <div className="thermal-label-inner">
      {settings.showName && product.name && (
        <div className="thermal-label-name">
          {product.name}
        </div>
      )}
      
      <div className="thermal-label-barcode">
        {settings.barcodeFormat === 'QR' ? (
          qrDataUrl && (
            <img 
              src={qrDataUrl} 
              alt="QR" 
              style={{ 
                width: '12mm', 
                height: '12mm',
                imageRendering: 'pixelated' // Sharp pixels for thermal printer
              }}
            />
          )
        ) : (
          barcodeDataUrl && (
            <img 
              src={barcodeDataUrl} 
              alt="Barcode"
              style={{ 
                maxWidth: '100%',
                maxHeight: variantText(product) ? '10mm' : '12mm',
                imageRendering: 'crisp-edges' // Sharp edges for barcode
              }}
            />
          )
        )}
      </div>
      
      {settings.showPrice && product.price && (
        <div className="thermal-label-price">
          {formatPrice(product.price)}
        </div>
      )}
      
      {variantText(product) && (
        <div className="thermal-label-desc" style={{ fontWeight: 700 }}>
          {variantText(product)}
        </div>
      )}

      {settings.showDescription && product.description && (
        <div className="thermal-label-desc">
          {product.description}
        </div>
      )}
    </div>
  );
}


// Real Size Label for Preview - Shows exact dimensions with high resolution
function RealSizeLabel({ product, settings }) {
  const [barcodeDataUrl, setBarcodeDataUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    if (settings.barcodeFormat === 'QR') {
      QRCode.toDataURL(product.code || '0000', {
        width: 200,
        margin: 2,
        errorCorrectionLevel: 'H'
      }).then(setQrDataUrl);
    } else {
      if (product.code) {
        try {
          const canvas = document.createElement('canvas');
          JsBarcode(canvas, product.code, {
            format: settings.barcodeFormat,
            width: 2.5,
            height: 60,
            displayValue: settings.showCode,
            fontSize: 18,
            margin: 10,
            textAlign: 'center',
            textMargin: 4,
            background: '#ffffff',
            lineColor: '#000000'
          });
          setBarcodeDataUrl(canvas.toDataURL('image/png', 1.0));
        } catch (e) {
          console.error('Barcode error:', e);
        }
      }
    }
  }, [product, settings]);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      textAlign: 'center',
      overflow: 'hidden'
    }}>
      {settings.showName && product.name && (
        <div style={{ 
          fontSize: '8pt', 
          fontWeight: 'bold', 
          marginBottom: '1mm',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%'
        }}>
          {product.name}
        </div>
      )}
      
      {settings.barcodeFormat === 'QR' ? (
        qrDataUrl && (
          <img 
            src={qrDataUrl} 
            alt="QR" 
            style={{ width: '12mm', height: '12mm' }}
          />
        )
      ) : (
        barcodeDataUrl && (
          <img 
            src={barcodeDataUrl} 
            alt="Barcode"
            style={{ maxWidth: '95%', maxHeight: variantText(product) ? '11mm' : '15mm' }}
          />
        )
      )}
      
      {settings.showPrice && product.price && (
        <div style={{ 
          fontSize: '10pt', 
          fontWeight: 'bold', 
          marginTop: '0.5mm' 
        }}>
          {formatPrice(product.price)}
        </div>
      )}
      
      {variantText(product) && (
        <div style={{ fontSize: '6pt', fontWeight: 700, marginTop: '0.5mm', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
          {variantText(product)}
        </div>
      )}

      {settings.showDescription && product.description && (
        <div style={{ 
          fontSize: '6pt', 
          marginTop: '0.5mm',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%'
        }}>
          {product.description}
        </div>
      )}
    </div>
  );
}
